import { useAppSelector } from '@hooks/app/redux';
import { getNextPageParam } from '@lib/utils/reactQuery/infinityQueryHelpers';
import { searchClients } from '@services/external/client.api';
import React from 'react';
import { useInfiniteQuery, UseInfiniteQueryOptions } from 'react-query';
import { IClientSearchParams } from 'types/client.types';

const PAGE_SIZE = 20;
type QParams = Omit<IClientSearchParams, 'page' | 'pageSize'> & { providerId: string };

async function handler(page: number = 1, params: QParams) {
  const { data } = await searchClients(params.providerId, { page, pageSize: PAGE_SIZE, ...params });

  return data.data;
}

type TQueryData = Awaited<ReturnType<typeof handler>>;

function useClientSearchList(
  params: Omit<QParams, 'providerId'> = { direction: 'DESC', sortBy: 'createdAt', myClients: true },
  options?: Omit<UseInfiniteQueryOptions<TQueryData, any, TQueryData, TQueryData, any>, 'queryKey'>,
) {
  const providerId = useAppSelector((state) => state.users.currentProvider?.id)!;

  const { data, ...rest } = useInfiniteQuery(
    ['clients:searchList', params, providerId],
    ({ pageParam }) => handler(pageParam, { ...params, providerId }),
    {
      ...options,
      getNextPageParam: getNextPageParam,
    },
  );

  const flattenData = React.useMemo(() => {
    return data?.pages.flatMap((p) => p.items) ?? [];
  }, [data]);

  return { data: flattenData, original: data, ...rest };
}

export default useClientSearchList;
